import { decode } from 'html-entities';
import type { Economics } from '@benzinga/calendar-economics-manager';
import type { StoryObject } from '@benzinga/advanced-news-manager';
import type { SimpleNewsOptions, SimpleNewsQuery } from '@benzinga/internal-news-manager';

export interface SeriesPoint {
  actual: number | null;
  consensus: number | null;
  prior: number | null;
}

export interface EconomicsRelease {
  isoDate: string;
  releaseDate: string;
  period: string;
  series: Record<string, SeriesPoint>;
}

export const parseNum = (value: string | undefined | null): number | null => {
  if (!value) return null;
  const parsed = parseFloat(value);
  return isNaN(parsed) ? null : parsed;
};

export const hasNumericActual = (event: Economics): boolean => parseNum(event.actual) != null;

export const formatReleaseDate = (date: string): string =>
  new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });

export const mergeSeriesPoint = (
  existing: SeriesPoint | undefined,
  incoming: SeriesPoint,
): SeriesPoint => {
  if (!existing) return incoming;
  const [authoritative, fallback] =
    incoming.actual != null ? [incoming, existing] : [existing, incoming];
  return {
    actual: authoritative.actual ?? fallback.actual,
    consensus: authoritative.consensus ?? fallback.consensus,
    prior: authoritative.prior ?? fallback.prior,
  };
};

export const mergeReleasesByDate = (
  rawBySeries: Economics[][],
  seriesNames: string[],
): Map<string, EconomicsRelease> => {
  const byDate = new Map<string, EconomicsRelease>();

  rawBySeries.forEach((events, idx) => {
    const eventName = seriesNames[idx];
    const isPrimary = idx === 0;

    events.forEach(event => {
      if (!event.date) return;
      const isoDate = event.date.split('T')[0];
      const period =
        event.event_period && event.period_year != null ? `${event.event_period} ${event.period_year}` : '';
      const releaseDate = formatReleaseDate(event.date);

      if (!byDate.has(isoDate)) {
        byDate.set(isoDate, { isoDate, releaseDate, period, series: {} });
      } else if (isPrimary && period) {
        byDate.get(isoDate)!.period = period;
      }

      const incoming: SeriesPoint = {
        actual: parseNum(event.actual),
        consensus: parseNum(event.consensus),
        prior: parseNum(event.prior),
      };
      const bucket = byDate.get(isoDate)!;
      bucket.series[eventName] = mergeSeriesPoint(bucket.series[eventName], incoming);
    });
  });

  return byDate;
};

export const selectReleaseHistory = (
  byDate: Map<string, EconomicsRelease>,
  primaryName: string,
  targetHistory: number,
): EconomicsRelease[] =>
  [...byDate.values()]
    .filter(release => release.series[primaryName]?.actual != null)
    .sort((left, right) => left.isoDate.localeCompare(right.isoDate))
    .slice(-targetHistory);

export const selectNextRelease = (
  rawPrimary: Economics[],
  byDate: Map<string, EconomicsRelease>,
  primaryName: string,
  todayUtcISO: string,
): Economics | null =>
  rawPrimary
    .filter(event => {
      if (hasNumericActual(event) || !event.date || event.date.slice(0, 10) < todayUtcISO) return false;
      const iso = event.date.split('T')[0];
      return byDate.get(iso)?.series[primaryName]?.actual == null;
    })
    .sort((left, right) => (left.date ?? '').localeCompare(right.date ?? ''))[0] ?? null;

export const ECONOMICS_NEWS_QUERY: SimpleNewsQuery = { channels: ['Economics'] };
export const ECONOMICS_NEWS_OPTIONS: SimpleNewsOptions = { displayOutput: 'abstract', excludeNewsdesk: true, limit: 5 };

export const toPlainNewsText = (value: string | null | undefined): string =>
  value ? decode(value.replace(/<[^>]*>/g, '')).trim() : '';

export const normalizeNewsStories = (stories: StoryObject[]): StoryObject[] =>
  stories.map(story => ({
    ...story,
    teaser: toPlainNewsText(story.teaser),
    title: toPlainNewsText(story.title),
  }));
